import React, { useState, useEffect } from 'react';

export interface ScanRow {
  Ticker: string;
  Price: number;
  Dist_52W_pct: number;
  Dist_EMA_pct: number;
  ATR_Expanding: boolean;
  Vol_x_Avg: number;
  Vol_1_5x: boolean;
  Gap_pct: number;
  Gap_Up: boolean;
  SCORE: number;
  Zone: 'AT_BREAKOUT' | 'BUILDING' | 'PULLBACK';
  MktCap_B: number;
  EPS: number;
  RevGrowth_pct: number;
  Sector: string;
}

interface ScanData {
  rows: ScanRow[];
  scanDate: string;
  generatedAt: string;
}

interface PastGainsData {
  rows: Array<{
    ticker: string;
    entryDate: string;
    entryPrice: number;
    currentPrice: number;
    gainPct: number;
    lastScanDate: string;
    score: number;
  }>;
  generatedAt: string;
}

async function fetchScanData(): Promise<ScanData> {
  const res = await fetch(`${window.__LIVE_API_BASE || ''}/api/breakout-scan`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<ScanData>;
}

function fmt(v: number | null | undefined, d = 2): string {
  if (v == null) return '--';
  return v.toFixed(d);
}

function fmtDate(s: string): string {
  if (!s) return '--';
  // Handle YYYYMMDD format from CSV
  const normalized = s.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3');
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }).replace(/ /g, '-');
}

type SortKey = keyof ScanRow;
type SortDir = 'asc' | 'desc';

function deltaColor(v: number): string {
  if (v > 0) return '#22c55e';
  if (v < 0) return '#ef4444';
  return '#94a3b8';
}

function scoreColor(s: number): string {
  if (s >= 60) return '#22c55e';
  if (s >= 45) return '#eab308';
  return '#94a3b8';
}

const SCORE_COMPONENTS = [
  { label: 'Momentum',      desc: 'Clarity of 52W proximity',      sub: '(3 - dist_52W) * 3',       max: 9  },
  { label: 'Trend Strength',desc: 'Distance above 20 EMA',         sub: 'min(dist_EMA, 10) * 2',    max: 20 },
  { label: 'Volume',        desc: 'Vol vs 20-day avg',              sub: 'min(vol_ratio/2, 1) * 30', max: 30 },
  { label: 'ATR Expansion', desc: 'ATR vs 20-day ATR',              sub: 'min((atr_ratio-0.9)*50, 20)', max: 20 },
  { label: 'Gap Up',        desc: "Today's gap %",                  sub: 'min(gap_pct*3, 15)',       max: 15 },
  { label: 'RS vs SPY',     desc: 'Outperformance vs market',       sub: 'max(SPY_change*2, 0)',     max: 6  },
];

const BreakoutScanPanel: React.FC = () => {
  const [data, setData] = useState<ScanData | null>(null);
  const [pastGains, setPastGains] = useState<PastGainsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('SCORE');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [activeZone, setActiveZone] = useState<'ALL' | 'AT_BREAKOUT' | 'BUILDING' | 'PULLBACK'>('ALL');

  useEffect(() => {
    fetchScanData()
      .then(d => { setData(d); setError(null); })
      .catch(e => setError(e.message));

    fetch(`${window.__LIVE_API_BASE || ''}/api/past-scan-gains`)
      .then(r => r.json())
      .then(d => setPastGains(d))
      .catch(() => setPastGains(null));
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (error) {
    return (
      <div style={styles.errorBox}>
        Failed to load scan data: {error}
      </div>
    );
  }

  if (!data) {
    return <div style={styles.loading}>Loading breakout scan...</div>;
  }

  const filtered = data.rows.filter(r => {
    if (activeZone === 'ALL') return true;
    return r.Zone === activeZone;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey as keyof ScanRow];
    const bv = b[sortKey as keyof ScanRow];
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    if (typeof av === 'string' && typeof bv === 'string') {
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return 0;
  });

  const breakoutCount = data.rows.filter(r => r.Zone === 'AT_BREAKOUT').length;
  const buildingCount = data.rows.filter(r => r.Zone === 'BUILDING').length;
  const pullbackCount = data.rows.filter(r => r.Zone === 'PULLBACK').length;

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span style={{ marginLeft: 4, opacity: sortKey === col ? 1 : 0.3 }}>
      {sortKey === col ? (sortDir === 'desc' ? '\u2193' : '\u2191') : '\u2194'}
    </span>
  );

  return (
    <div style={styles.wrap}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Weekly Breakout Scan</div>
          <div style={styles.subtitle}>
            Scan generated: {fmtDate(data.scanDate)} &nbsp;&middot;&nbsp; {data.rows.length} stocks passed filters
          </div>
        </div>
        <div style={styles.zoneBadges}>
          <button
            style={{ ...styles.zoneBadge, ...(activeZone === 'ALL' ? styles.zoneBadgeActive : {}) }}
            onClick={() => setActiveZone('ALL')}
          >
            All ({data.rows.length})
          </button>
          <button
            style={{ ...styles.zoneBadge, ...(activeZone === 'AT_BREAKOUT' ? styles.zoneBadgeActive : {}) }}
            onClick={() => setActiveZone('AT_BREAKOUT')}
          >
            At Breakout ({breakoutCount})
          </button>
          <button
            style={{ ...styles.zoneBadge, ...(activeZone === 'PULLBACK' ? styles.zoneBadgeActive : {}) }}
            onClick={() => setActiveZone('PULLBACK')}
          >
            Pullback ({pullbackCount})
          </button>
          <button
            style={{ ...styles.zoneBadge, ...(activeZone === 'BUILDING' ? styles.zoneBadgeActive : {}) }}
            onClick={() => setActiveZone('BUILDING')}
          >
            Building ({buildingCount})
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {[
                { key: 'Ticker',        label: 'Ticker',       align: 'left' as const },
                { key: 'Price',         label: 'Price',        align: 'right' as const },
                { key: 'SCORE',         label: 'Score',        align: 'right' as const },
                { key: 'Dist_52W_pct',  label: '\u00b152W %',  align: 'right' as const },
                { key: 'Vol_x_Avg',     label: 'Vol',          align: 'right' as const },
                { key: 'Zone',          label: 'Zone',         align: 'center' as const },
                { key: 'EPS',           label: 'EPS',          align: 'right' as const },
                { key: 'RevGrowth_pct', label: 'Rev Growth',   align: 'right' as const },
                { key: 'MktCap_B',      label: 'Mkt Cap',      align: 'right' as const },
                { key: 'Sector',        label: 'Sector',       align: 'left' as const },
              ].map(col => (
                <th
                  key={col.key}
                  style={{ ...styles.th, textAlign: col.align, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(col.key as SortKey)}
                >
                  {col.label}<SortIcon col={col.key as SortKey} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr key={row.Ticker} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                <td style={{ ...styles.td, fontWeight: 700, color: '#e2e8f0' }}>
                  {row.Ticker}
                  {row.ATR_Expanding && <span style={styles.flagEmoji}> *</span>}
                  {row.Gap_Up && <span style={styles.flagEmoji}> ^</span>}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  ${fmt(row.Price)}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: scoreColor(row.SCORE), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(row.SCORE)}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: deltaColor(row.Dist_52W_pct), fontVariantNumeric: 'tabular-nums' }}>
                  {row.Dist_52W_pct > 0 ? '+' : ''}{fmt(row.Dist_52W_pct)}%
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: row.Vol_1_5x ? '#22c55e' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(row.Vol_x_Avg)}x
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{
                    ...styles.zoneTag,
                    background: row.Zone === 'AT_BREAKOUT' ? 'rgba(34,197,94,0.15)' : row.Zone === 'PULLBACK' ? 'rgba(41,52,255,0.15)' : 'rgba(234,179,8,0.15)',
                    color: row.Zone === 'AT_BREAKOUT' ? '#22c55e' : row.Zone === 'PULLBACK' ? '#60a5fa' : '#eab308',
                  }}>
                    {row.Zone === 'AT_BREAKOUT' ? 'AT BREAKOUT' : row.Zone === 'PULLBACK' ? 'PULLBACK' : 'BUILDING'}
                  </span>
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                  ${fmt(row.EPS)}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(row.RevGrowth_pct)}%
                </td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                  ${fmt(row.MktCap_B)}B
                </td>
                <td style={{ ...styles.td, color: '#64748b', fontSize: 11 }}>
                  {row.Sector}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.footer}>
        * = ATR expanding &nbsp;|&nbsp; ^ = Gap up today &nbsp;|&nbsp; Click column headers to sort
      </div>

      {/* Past Picks — Profitable from Prior Weeks */}
      {pastGains && pastGains.rows.length > 0 && (
        <div style={styles.pastSection}>
          <div style={styles.pastTitle}>
            📊 Past Scan Picks — Still in Profit ({pastGains.rows.length} stocks)
          </div>
          <div style={styles.pastSubtitle}>
            Stocks featured on prior weeks' scans that are currently green. Sorted by gain %.
          </div>
          <div style={styles.pastTableWrap}>
            <table style={styles.pastTable}>
              <thead>
                <tr>
                  <th style={styles.pastTh}>Ticker</th>
                  <th style={styles.pastTh}>Entry</th>
                  <th style={styles.pastTh}>Current</th>
                  <th style={styles.pastTh}>Gain</th>
                  <th style={styles.pastTh}>First Seen</th>
                  <th style={styles.pastTh}>Last Scan</th>
                  <th style={styles.pastTh}>Score</th>
                </tr>
              </thead>
              <tbody>
                {pastGains.rows.map((row, i) => (
                  <tr key={row.ticker} style={i % 2 === 0 ? styles.pastTrEven : styles.pastTrOdd}>
                    <td style={styles.pastTdBold}>{row.ticker}</td>
                    <td style={styles.pastTd}>${row.entryPrice.toFixed(2)}</td>
                    <td style={styles.pastTd}>${row.currentPrice.toFixed(2)}</td>
                    <td style={{ ...styles.pastTd, color: '#22c55e', fontWeight: 700 }}>+{row.gainPct.toFixed(1)}%</td>
                    <td style={styles.pastTd}>{fmtDate(row.entryDate)}</td>
                    <td style={styles.pastTd}>{fmtDate(row.lastScanDate)}</td>
                    <td style={{ ...styles.pastTd, color: '#94a3b8' }}>{row.score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Methodology */}
      <div style={styles.methodWrap}>
        <div style={styles.methodTitle}>How the Scan Works</div>

        <div style={styles.methodSection}>
          <div style={styles.methodSubTitle}>FILTER CRITERIA</div>
          <div style={styles.methodGrid}>
            <span>Price &gt; $10</span>
            <span>Volume &gt; 500K shares</span>
            <span>Above 20-day EMA</span>
            <span>Market Cap &gt; $5B</span>
            <span>EPS &gt; $0</span>
            <span>Revenue Growth &gt; 5%</span>
          </div>
        </div>

        <div style={styles.methodSection}>
          <div style={styles.methodSubTitle}>COMPOSITE SCORE (0-100)</div>
          <div style={styles.scoreBars}>
            {SCORE_COMPONENTS.map(item => (
              <div key={item.label} style={styles.scoreBarRow}>
                <div style={styles.scoreBarLabel}>
                  <span style={styles.scoreBarName}>{item.label}</span>
                  <span style={styles.scoreBarDesc}>{item.desc}</span>
                </div>
                <div style={styles.scoreBarTrack}>
                  <div style={{ ...styles.scoreBarFill, width: `${item.max}%` }} />
                </div>
                <div style={styles.scoreBarSub}>{item.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.methodSection}>
          <div style={styles.methodSubTitle}>ZONE DEFINITIONS</div>
          <div style={styles.zoneDefs}>
            <div>
              <span style={{ color: '#60a5fa', fontWeight: 700 }}>PULLBACK</span>
              <span style={styles.methodText}> — within 1% of 52W, ATR expanding. Stock already broke out, now pulling back to retest.</span>
            </div>
            <div>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>AT BREAKOUT</span>
              <span style={styles.methodText}> — within 0-2% of 52-week high. Stock is at the trigger point.</span>
            </div>
            <div>
              <span style={{ color: '#eab308', fontWeight: 700 }}>BUILDING</span>
              <span style={styles.methodText}> — 2-5% below 52W. Accumulating momentum, approaching breakout.</span>
            </div>
          </div>
        </div>

        <div style={styles.methodSection}>
          <div style={styles.methodText}>
            Inspired by Turtle S1 breakout rules, Marty Schwartz champion system, MTP momentum framework, and Nicolas Darvas box theory.
            Data sourced from Yahoo Finance. Scan runs weekly every Monday at 8PM SGT.
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: { padding: '0 16px 16px' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '16px 0 12px', flexWrap: 'wrap', gap: 12,
  },
  title: { fontSize: 18, fontWeight: 700, color: '#f1f5f9' },
  subtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  zoneBadges: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  zoneBadge: {
    background: 'rgba(30,35,60,0.8)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
    color: '#94a3b8', transition: 'all 0.15s',
  },
  zoneBadgeActive: { background: 'rgba(41,52,255,0.25)', border: '1px solid rgba(41,52,255,0.5)', color: '#a5b4fc' },
  tableWrap: { overflowX: 'auto', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: {
    padding: '8px 12px', background: 'rgba(15,17,30,0.9)', color: '#64748b',
    fontWeight: 600, letterSpacing: '0.03em', whiteSpace: 'nowrap',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  trEven: { background: 'rgba(18,20,38,0.4)' },
  trOdd:  { background: 'rgba(18,20,38,0.2)' },
  td:     { padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.03)', whiteSpace: 'nowrap' },
  flagEmoji: { fontSize: 10, marginLeft: 3 },
  zoneTag: { padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 },
  loading:  { padding: 40, textAlign: 'center', color: '#64748b' },
  errorBox: { padding: 20, color: '#ef4444', background: 'rgba(239,68,68,0.1)', borderRadius: 8, margin: 16 },
  footer:   { padding: '8px 12px', fontSize: 11, color: '#475569', borderTop: '1px solid rgba(255,255,255,0.04)' },

  methodWrap:    { margin: '24px 0 0', padding: '16px', background: 'rgba(15,17,30,0.6)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10 },
  methodTitle:   { fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 16 },
  methodSection: { marginBottom: 16 },
  methodSubTitle:{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, letterSpacing: '0.06em' },
  methodGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12, color: '#64748b' },
  methodText:    { fontSize: 11, color: '#475569', lineHeight: 1.6 },
  scoreBars:     { display: 'flex', flexDirection: 'column', gap: 7 },
  scoreBarRow:   { display: 'grid', gridTemplateColumns: '130px 1fr 200px', alignItems: 'center', gap: 8 },
  scoreBarLabel: { display: 'flex', flexDirection: 'column' },
  scoreBarName:  { fontSize: 12, color: '#94a3b8', fontWeight: 600 },
  scoreBarDesc:  { fontSize: 10, color: '#475569' },
  scoreBarTrack: { height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 },
  scoreBarFill:  { height: '100%', background: 'rgba(41,52,255,0.5)', borderRadius: 2 },
  scoreBarSub:   { fontSize: 10, color: '#334155', fontFamily: 'monospace' },
  zoneDefs:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 },

  pastSection:    { marginTop: 28 },
  pastTitle:      { fontSize: 16, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 },
  pastSubtitle:   { fontSize: 12, color: '#64748b', marginBottom: 12 },
  pastTableWrap:  { overflowX: 'auto', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' },
  pastTable:      { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  pastTh:         { padding: '8px 12px', background: 'rgba(15,17,30,0.9)', color: '#64748b', fontWeight: 600, letterSpacing: '0.03em', whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'left' },
  pastTrEven:     { background: 'rgba(18,20,38,0.4)' },
  pastTrOdd:      { background: 'rgba(18,20,38,0.2)' },
  pastTd:         { padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.03)', whiteSpace: 'nowrap', color: '#94a3b8' },
  pastTdBold:     { padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.03)', whiteSpace: 'nowrap', fontWeight: 700, color: '#e2e8f0' },
};

export default BreakoutScanPanel;
